import React, { useContext, useEffect, useState } from 'react';
import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import { DESCRIPTION_TYPES, SUBMIT_TYPES } from '../consts/consts';
import { Form } from '../../../../generic-components/Form';
import { useUpdatePaymentOptionMutation } from '../../../../hooks/mutations/useUpdatePaymentOptionMutation';
import { useStore } from '../../../../store';
import { useDeletePaymentOption } from '../../../../hooks/mutations/useDeletePamentOption';
import { OnboardingContext } from '../InvoiceOnboarding';

interface IProps {
  method: any;
  handleToggleExpand: () => void;
  triggerReFetchOptionsList: () => void;
}

export const GenericPaymentBody = ({ method, handleToggleExpand, triggerReFetchOptionsList }: IProps) => {
  const [connectionValue, setConnectionValue] = useState<string>(method.link);
  const [editConnectionValue, setEditConnectionValue] = useState<string>('');
  const [isEditPaymentActionsShow, setIsEditPaymentActionsShow] = useState<boolean>(false);
  const {
    user: { customerId },
  } = useStore((state) => state);
  const flowContext = useContext(OnboardingContext);
  const { onStepSubmit } = flowContext;

  const { mutateAsync: updatePayment, isLoading: isLoadingUpdate } = useUpdatePaymentOptionMutation({
    customerId,
    optionType: method.type,
    link: connectionValue,
    onSuccess: (data) => {
      onStepSubmit({ type: SUBMIT_TYPES.PAYMENTS.CONNECTION_SUCCESS, data: data.customerOptionInfo.type });
    },
  });

  const { mutateAsync: deletePayment, isLoading: isLoadingDelete } = useDeletePaymentOption({
    customerId,
    type: method.type,
  });

  const createMarkup = () => ({ __html: method.description });

  const handleFocus = () => {
    setIsEditPaymentActionsShow(true);
  };

  const handleSubmit = (data: any) => {
    if (method.activated) {
      deletePayment().then(() => triggerReFetchOptionsList());
    } else {
      const inputValue = data[method.type].value;
      setConnectionValue(inputValue);
    }
  };

  const handleFormDataChange = (data: any) => {
    if (!method.activated) return;
    const iValue = data[method.type].value;
    setEditConnectionValue(iValue);
  };

  const handleEditConnectionValue = () => {
    setConnectionValue(editConnectionValue);
  };

  const getDynamicInput = () => [
    {
      TITLE: method.appLinkHint || 'Username',
      TYPE: method.type,
      VALIDATION: method.activated ? '' : /^.{1,}$/,
      ERROR: method.activated ? '' : 'Connection value cannot be empty',
      AUTOCOMPLETE: 'off',
      MAX_LENGTH: 100,
      VALUE: method.link ? editConnectionValue || connectionValue : '',
      DISABLED: false,
      REPLACE: ' ',
      INITIAL_FORMAT: true,
    },
  ];

  useEffect(() => {
    if (!connectionValue || connectionValue === method.link) return;
    updatePayment().then(() => {
      setIsEditPaymentActionsShow(false);
      triggerReFetchOptionsList();
    });
  }, [connectionValue]);

  return (
    <div className='flex flex-col items-center w-full fadeIn'>
      {!method.activated && method.descriptionType === DESCRIPTION_TYPES.HTML && (
        <div
          className='max-w-[90%] py-[16px] px-[16px] text-center font-sans text-[16px] text-bw07'
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={createMarkup()}
        />
      )}
      {!method.activated && method.descriptionType === DESCRIPTION_TYPES.TEXT && (
        <div className='max-w-[90%] py-[16px] px-[16px] text-center font-sans text-[16px] text-bw07'>
          {method.description}
        </div>
      )}
      {method.activated && (
        <div className='max-w-[90%] py-[16px] px-[16px] mr-auto font-sans text-[16px] text-bw07'>
          Account Information:
        </div>
      )}
      <div className='w-full border-t bg-[#FBFBFD] border-bw03 py-[20px] px-[16px] rounded-b-[8px]'>
        <Form
          submitButtonText={method.activated ? `Delete ${method.appName} Connect` : `Connect ${method.appName}`}
          inputsList={getDynamicInput()}
          onSubmit={handleSubmit}
          isLoading={isLoadingUpdate || isLoadingDelete}
          isSuccess={false}
          isError={false}
          error=''
          onInputsFocus={handleFocus}
          onFormDataChange={handleFormDataChange}
          additionalActions={
            isEditPaymentActionsShow && method.activated ? (
              <div className='w-fit flex gap-4 items-center mb-[26px] mt-[12px] ml-auto fadeIn'>
                <LiliButton
                  isLoading={isLoadingUpdate}
                  type={LiliButtonType.SECONDARY}
                  onClick={handleToggleExpand}
                  title='Cancel'
                  id='editPaymentCancelButton'
                />
                <LiliButton
                  isLoading={isLoadingUpdate}
                  onClick={handleEditConnectionValue}
                  title='Save'
                  id='editPaymentSaveButton'
                />
              </div>
            ) : (
              ''
            )
          }
        />
      </div>
    </div>
  );
};
